<template>
  <m-card
    class="metric-color-editor"
    padding-small
  >
    <m-card-item
      class="_dynamic-toggle"
      @click="toggleDynamic(!dynamicEnabled)"
    >
      {{ $t('metricColorEditor.dynamicColor') }}
      <template #right>
        <m-switch
          :value="dynamicEnabled"
          small
        />
      </template>
    </m-card-item>
    <div
      v-if="!dynamicEnabled"
      class="_static"
    >
      <color-swatches
        :selected-color="localColor"
        @select="selectStaticColor"
      />
    </div>
    <div
      v-else
      class="_dynamic"
    >
      <m-draggable
        class="_rules"
        draggable-item-class="_drag-handle"
        ghost-item-class="_rule"
        dragover-item-class="_rule"
        can-drag-over-top
        can-drag-over-bottom
        :drag-between-height="30"
        :disabled="localRules.length <= 1"
        :recreate-key="localRules.map(({ id }) => id)"
        @set-drag-item="setDragItem"
        @over-top="setOverTop"
        @over-bottom="setOverBottom"
        @drag-drop="handleDrop"
        @cancel="cancelDragging"
      >
        <div
          v-for="(rule, index) in localRules"
          :key="rule.id"
          :data-id="rule.id"
          class="_rule"
        >
          <div
            v-if="dragItemId !== '' && draggingOverTop.includes(rule.id) && index === 0"
            class="_drag-over-top"
          />
          <div class="_rule-inner">
            <div class="_rule-header">
              <div class="_rule-condition">
                <m-btn
                  class="_drag-handle"
                  :disabled="localRules.length <= 1"
                  icon="drag"
                  hide-border
                  fab
                  light
                  xs
                />
                <span class="_rule-label">{{ $t('metricColorEditor.whenValue') }}</span>
                <m-dropdown
                  :title="$t('metricColorEditor.operator')"
                  close-on-click
                >
                  <m-btn
                    xs
                    hide-border
                    light
                  >
                    {{ operatorSymbolMap[rule.operator] }}
                    <m-icon
                      type="down"
                      size="11"
                      :color="$colors.grey.lighten2"
                      :style="{ marginLeft: '0.4rem' }"
                    />
                  </m-btn>
                  <template #overlay>
                    <m-card
                      list
                      no-padding
                    >
                      <m-card-item
                        v-for="op in operatorOptions"
                        :key="op.value"
                        :class="{ '-active': op.value === rule.operator }"
                        @click="updateRule(index, 'operator', op.value)"
                      >
                        {{ op.symbol }} {{ op.text }}
                      </m-card-item>
                    </m-card>
                  </template>
                </m-dropdown>
              </div>
              <m-btn
                icon="delete"
                hide-border
                light
                fab
                xs
                @click="removeRule(index)"
              />
            </div>
            <m-input-number
              :value="rule.value"
              :placeholder="$t('metricColorEditor.value')"
              small
              full-width
              @update:value="updateRule(index, 'value', $event)"
            />
            <m-dropdown
              :title="$t('metricColorEditor.colorLabel')"
              close-on-click
              class="_rule-color"
            >
              <m-card-item class="_rule-color-row">
                {{ $t('metricColorEditor.colorLabel') }}
                <template #right>
                  <div class="_color-trigger">
                    <div
                      class="_color-dot"
                      :style="colorDotStyle(rule.color || 'default')"
                    />
                    <span class="_color-name">{{ colorLabel(rule.color || 'default') }}</span>
                    <m-icon
                      type="down"
                      size="11"
                      :color="$colors.grey.lighten1"
                    />
                  </div>
                </template>
              </m-card-item>
              <template #overlay>
                <m-card padding-small>
                  <color-swatches
                    :selected-color="rule.color || 'default'"
                    @select="updateRule(index, 'color', $event)"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </div>
          <div
            v-if="dragItemId !== '' && draggingOverBottom.includes(rule.id)"
            class="_drag-over-bottom"
          />
        </div>
      </m-draggable>
      <m-btn
        block
        icon="plus"
        @click="addRule"
      >
        {{ $t('metricColorEditor.addRule') }}
      </m-btn>
    </div>
  </m-card>
</template>

<script setup>
import ColorSwatches from '@/components/custom-grid/ColorSwatches.vue';
import useSort from '@/composables/draggable/sort';
import { coloringMode as coloringModeEnum, coloringRuleOperator } from 'shared/constants.json';
import { computed, ref, watch } from 'vue';
import { getColor, optionTextColorMap } from 'shared/lib/color-map';
import { guid } from 'shared/lib/uuid';
import { shadeColor } from 'shared/lib/style';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  color: {
    type: String,
    default: 'default',
  },
  coloringRules: {
    type: Array,
    default: () => [],
  },
  coloringMode: {
    type: String,
    default: coloringModeEnum.static,
    validator: (value) => Object.values(coloringModeEnum).includes(value),
  },
});

const emit = defineEmits(['update:color', 'update:coloringRules', 'update:coloringMode']);

const toLocal = (rules) => (rules || []).map((r) => ({ ...r, id: r.uid ?? guid() }));

const localColor = ref(props.color || 'default');
const localRules = ref(toLocal(props.coloringRules));
const dynamicEnabled = computed(() => props.coloringMode === coloringModeEnum.dynamic);

watch(() => props.color, (val) => {
  localColor.value = val || 'default';
});
watch(() => props.coloringRules, (val) => {
  const incoming = val || [];
  // Only re-init if the rules actually changed externally (e.g. after save)
  const currentUids = localRules.value.filter((r) => r.uid).map((r) => String(r.uid));
  const incomingUids = incoming.filter((r) => r.uid).map((r) => String(r.uid));
  const changed = currentUids.length !== incomingUids.length
    || currentUids.some((uid, i) => uid !== incomingUids[i]);
  if (changed) {
    localRules.value = toLocal(incoming);
  }
});

const operatorSymbolMap = {
  [coloringRuleOperator.greaterThan]: '>',
  [coloringRuleOperator.greaterThanOrEqual]: '≥',
  [coloringRuleOperator.lessThan]: '<',
  [coloringRuleOperator.lessThanOrEqual]: '≤',
  [coloringRuleOperator.equal]: '=',
};

const operatorOptions = [
  { text: t('metricColorEditor.operators.greaterThan'), symbol: '>', value: coloringRuleOperator.greaterThan },
  { text: t('metricColorEditor.operators.greaterThanOrEqual'), symbol: '≥', value: coloringRuleOperator.greaterThanOrEqual },
  { text: t('metricColorEditor.operators.lessThan'), symbol: '<', value: coloringRuleOperator.lessThan },
  { text: t('metricColorEditor.operators.lessThanOrEqual'), symbol: '≤', value: coloringRuleOperator.lessThanOrEqual },
  { text: t('metricColorEditor.operators.equal'), symbol: '=', value: coloringRuleOperator.equal },
];

const colorDotStyle = (c) => {
  const fallback = optionTextColorMap.default;
  const bg = c === 'default' ? fallback : getColor(c);
  return {
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '.3rem',
    flexShrink: 0,
    backgroundColor: bg,
    border: `1px solid ${c === 'default' ? fallback : shadeColor(bg, -10)}`,
  };
};

const colorLabel = (c) => t(`metricColorEditor.colors.${c}`);

const {
  setDragItem,
  setOverBottom,
  setOverTop,
  draggingOverBottom,
  draggingOverTop,
  dropItem,
  cancelDragging,
  dragItemId,
} = useSort('id');

const handleDrop = () => {
  localRules.value = dropItem(localRules.value);
  emitRules();
};

const selectStaticColor = (color) => {
  localColor.value = color;
  emit('update:color', color);
};

const emitRules = () => {
  const rules = localRules.value.map(({ id, ...rule }) => rule);
  emit('update:coloringRules', rules);
};

const toggleDynamic = (val) => {
  emit('update:coloringMode', val ? coloringModeEnum.dynamic : coloringModeEnum.static);
  if (val && localRules.value.length === 0) {
    localRules.value = [{ id: guid(), operator: coloringRuleOperator.greaterThan, value: 0, color: 'default' }];
    emitRules();
  }
};

const addRule = () => {
  localRules.value = [...localRules.value, { id: guid(), operator: coloringRuleOperator.greaterThan, value: 0, color: 'default' }];
  emitRules();
};

const removeRule = (index) => {
  localRules.value = localRules.value.filter((_, i) => i !== index);
  emitRules();
};

const updateRule = (index, field, value) => {
  localRules.value = localRules.value.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule));
  emitRules();
};
</script>

<style scoped lang="scss">
.metric-color-editor {
  min-width: 24rem;

  ._dynamic-toggle {
    padding-bottom: .8rem;
    margin-bottom: .8rem;
    border-bottom: 1px solid $border-color;
  }

  ._static {
    padding: .4rem;
  }

  ._dynamic {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  ._rules {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  ._drag-handle {
    cursor: grab;
  }

  ._drag-over-top {
    height: 2px;
    background-color: $primary-color;
    border-radius: 1px;
    margin-top: calc(-0.5rem - 1px);
    margin-bottom: calc(0.5rem - 1px);
  }

  ._drag-over-bottom {
    height: 2px;
    background-color: $primary-color;
    border-radius: 1px;
    margin-top: calc(0.5rem - 1px);
    margin-bottom: calc(-0.5rem - 1px);
  }

  ._rule {
    display: flex;
    flex-direction: column;
  }

  ._rule-inner {
    display: flex;
    flex-direction: column;
    gap: .4rem;
    padding: .8rem;
    background-color: map_get($grey, 'lighten-7');
    border-radius: .6rem;

    :deep(.m-input) {
      background-color: map_get($grey, 'lighten-6');
    }
  }

  ._rule-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  ._rule-condition {
    display: flex;
    align-items: center;
    gap: .4rem;
  }

  ._rule-label {
    font-size: $font-size-3;
    color: $font-color-secondary;
    white-space: nowrap;
  }

  ._rule-color {
    display: flex;
    width: calc(100% + .8rem);
    margin: 0 -.4rem;
  }

  ._rule-color-row {
    font-size: $font-size-3;
  }

  ._color-trigger {
    display: flex;
    align-items: center;
    gap: .6rem;
  }

  ._color-name {
    font-size: $font-size-3;
  }
}
</style>
